<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    :submitTitle="submitTitle"
    icon="fa-layer-plus"
    sizeClass="modal-xl"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" v-if="!loading">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-assembly-details">
            <i class="far fa-list-alt"></i> Assembly
          </Tab>
          <Tab tabid="tab-assembly-structure">
            <i class="far fa-layer-group"></i> Structure
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-assembly-details">
            <FormRow label="Stock">
              <FormSearchableSelect ref="stockCodeEntry" v-model="orderItemData.stockId" @input="stockItemUpdated" :options="assemblyStockLookupList" placeholder="Stock Code" field="stockId" v-if="isCreateMode" />
              <FormStaticLabel v-if="!isCreateMode">{{formatLookupName(orderItemData.stockId, assemblyStockLookupList)}}</FormStaticLabel>
            </FormRow>
            <div v-if="hasStock">
              <Spinner v-if="loadingStock" />
              <div v-if="hasStock && orderItemData.stock">
                <FormRow label="Stock Description">
                  <FormInputText v-model="orderItemData.description" placeholder="Description" field="description"></FormInputText>
                </FormRow>
                <FormRow label="Mark As">
                  <FormInputText v-model="orderItemData.mark" placeholder="Mark As" note="Name / Description for this item" field="mark"></FormInputText>
                </FormRow>
                <FormRow label="Notes">
                  <FormInputTextArea v-model="orderItemData.notes" placeholder="Additional Notes" note="Additional notes for production and processing"></FormInputTextArea>
                </FormRow>
                <FormHorizontalRule />
                <FormRow label="Quantity">
                  <FormInputText type="number" v-model="orderItemData.quantity" placeholder="Quantity" field="quantity" />
                </FormRow>
                <FormRow label="Initial Size" :fieldCount="2" v-if="isCreateMode && (isRectangleShape || isOvalShape)">
                  <template v-slot:field1>
                    <FormInputText type="number" v-model="initialHeight" placeholder="Height" prepend="Height" addon="mm" field="initialHeight" v-if="isSizeEditable" />
                    <FormStaticLabel v-if="!isSizeEditable">Height: {{initialHeight}}mm</FormStaticLabel>
                  </template>
                  <template v-slot:field2>
                    <FormInputText type="number" v-model="initialWidth" placeholder="Width" prepend="Width" addon="mm" field="initialWidth" v-if="isSizeEditable" />
                    <FormStaticLabel v-if="!isSizeEditable">Width: {{initialWidth}}mm</FormStaticLabel>
                  </template>
                </FormRow>
                <FormRow label="Initial Size" v-if="isCreateMode && (isCircleShape || isSemiCircleShape)">
                  <FormInputText type="number" v-model="initialRadius" placeholder="Radius" prepend="Radius" addon="mm" field="initialRadius" v-if="isSizeEditable" />
                  <FormStaticLabel v-if="!isSizeEditable">Radius: {{initialRadius}}mm</FormStaticLabel>
                </FormRow>
                <FormRow label="Stock Face Up" v-if="isStockUsingFaces">
                  <FormSelect v-model="orderItemData.stockFaceSideUpType" :options="StockFaceTypeLookup" placeholder="Stock Face Up" field="stockFaceSideUpType" />
                </FormRow>
                <FormRow label="Stock Grain Direction" v-if="isStockUsingGrain">
                  <FormSelect v-model="orderItemData.grainDirection" :options="StockGrainDirectionTypeLookup" placeholder="Stock Grain Direction" field="grainDirection" />
                </FormRow>

                <FormRow label="Unit Price">
                  <FormStaticLabel v-if="!orderItemData.unitPriceOverridden">
                    {{formatCurrency(orderItemData.unitPrice)}}
                    <a class="order-item-editable-button ml-2" @click.prevent="overrideUnitPrice()"><i class="far fa-pencil"></i></a>
                  </FormStaticLabel>
                  <FormInputText v-if="orderItemData.unitPriceOverridden" type="number" prepend="$" v-model="orderItemData.unitPrice" :addonSlot="true">
                    <template v-slot:addon>
                      <button data-toggle="dropdown" class="btn btn-white dropdown-toggle" type="button"></button>
                      <ul class="dropdown-menu">
                        <DropdownMenuItem icon="fa-undo-alt" @click="resetUnitPrice">Reset Unit Price</DropdownMenuItem>
                      </ul>
                    </template>
                  </FormInputText>
                </FormRow>
              </div>
            </div>
          </TabPage>
          <TabPage tabid="tab-assembly-structure">
            <div v-if="hasStock && !loadingStock">
              <div v-for="(childOrderItem, itemIndex) of assemblyOrderItemList" :key="childOrderItem.id" :style="assemblyOrderItemStyle(childOrderItem)">
                <div v-if="isCreateMode || orderItemIsParent(childOrderItem)">
                  <div class="mb-2">
                    <span class="font-weight-bold">Item #{{childOrderItem.levelIndex+1}} - {{formatLookupName(childOrderItem.stockType, InventoryStockTypeLookup)}}</span><br />
                    <template v-if="childOrderItem.assemblyDescription">
                      <span>{{childOrderItem.assemblyDescription}}</span><br />
                    </template>
                  </div>
                  <div v-if="!isCreateMode && !orderItemIsGeneral(childOrderItem)">
                    <p class="text-muted font-italic text-center">Access item details via the editor shape</p>
                  </div>
                  <div v-if="isCreateMode || orderItemIsGeneral(childOrderItem)">
                    <FormRow label="Stock">
                      <FormSearchableSelect ref="stockCodeEntry" v-model="childOrderItem.stockId" :options="orderItemStockLookupList(childOrderItem)" placeholder="Stock Code" @input="orderItemStockItemUpdated(childOrderItem)" :field="`item${itemIndex}-stockId`" />
                    </FormRow>
                    <Spinner v-if="childOrderItem.loadingStock" />
                    <div v-if="orderItemHasStock(childOrderItem) && !childOrderItem.loadingStock">
                      <FormRow label="Stock Description">
                        <FormInputText v-model="childOrderItem.description" placeholder="Description" field="description"></FormInputText>
                      </FormRow>
                      <FormRow label="Price Group" v-if="orderItemIsGlass(childOrderItem)">
                        <FormSelect v-model="childOrderItem.stockPriceGroupId" :options="orderItemPriceGroupLookupList(childOrderItem)" @input="orderItemPriceGroupUpdated(childOrderItem, $event)" placeholder="Price Group" :field="`item${itemIndex}-stockPriceGroupId`" />
                      </FormRow>
                      <FormRow label="Stamped" v-if="orderItemIsStampAvailable(childOrderItem)">
                        <FormCheckbox v-model="childOrderItem.isStamped" placeholder="Stamped" />
                      </FormRow>
                      <FormRow label="Initial Size" :fieldCount="2" v-if="(isRectangleShape || isOvalShape) && orderItemIsGlass(childOrderItem)">
                        <template v-slot:field1>
                          <FormInputText type="number" v-model="childOrderItem.height" placeholder="Height" prepend="Height" addon="mm" :field="`item${itemIndex}-height`" v-if="isSizeEditable" />
                          <FormStaticLabel v-if="!isSizeEditable">Height: {{childOrderItem.height}}mm</FormStaticLabel>
                        </template>
                        <template v-slot:field2>
                          <FormInputText type="number" v-model="childOrderItem.width" placeholder="Width" prepend="Width" addon="mm" :field="`item${itemIndex}-width`" v-if="isSizeEditable" />
                          <FormStaticLabel v-if="!isSizeEditable">Width: {{childOrderItem.width}}mm</FormStaticLabel>
                        </template>
                      </FormRow>
                      <FormRow label="Mark As" v-if="orderItemIsMarkAvailable(childOrderItem)">
                        <FormInputText v-model="childOrderItem.mark" placeholder="Mark As" note="Name / Description for this item"></FormInputText>
                      </FormRow>
                    </div>
                  </div>
                  <FormHorizontalRule v-if="itemIndex < assemblyOrderItemList.length-1" />
                </div>
              </div>
            </div>
            <p v-if="!hasStock">Assembly stock must be selected</p>
          </TabPage>
        </template>
      </TabbedBox>
    </FormGroup>

  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import Utils from '@/utils/utils'
import FilterUtils from '@/utils/filterUtils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import GlassLevelSelectPopover from '@/modules/inventory/components/glassLevelSelectPopover'

import { InventoryStockTypes, InventoryStockTypeLookup, PriceTypes, UOMTypes, StockFaceTypes, StockFaceTypeLookup, StockGrainDirectionTypes, StockGrainDirectionTypeLookup, FilterOptionTypes, StockAssemblyStructureTypes } from '@/store/types'
import { LibraryItemShapeTypes } from '../types'

export default {
  name: 'EditorAssemblyItemDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode,
    GlassLevelSelectPopover
  },
  props: {
    isCreateMode: Boolean,
    contactId: Number,
    defaultSalesRateId: Number,      // Note: Used if contactId is not set
    editorConfigData: Object
  },
  data() {
    return {
      dialogForm: {},
      orderItemData: {
        stockId: null,
        stockPriceGroupId: null,
        stock: null,
        glassLevelId: null,
        glassLevel: null,
        description: '',
        mark: '',
        notes: '',
        quantity: 1,
        thickness: 0,
        unitPrice: 0,
        unitPriceOverridden: false,
        minChargePrice: 0,
        minChargeQuantity: 0,
        minChargeInvoice: 0,
        grainDirection: StockGrainDirectionTypes.vertical,
        stockFaceSideUpType: StockFaceTypes.smooth
      },
      shapeType: '',
      initialWidth: 1000,
      initialHeight: 1000,
      initialRadius: 500,
      defaultPriceGroupId: 0,
      isStamped: false,
      isStampAvailable: false,
      assemblyStockList: [],
      assemblyStockLookupList: [],
      stockList: [],
      priceGroupList: [],
      globalDefaultEdgeworkList: [],
      stockDefaultEdgeworkList: [],
      loading: false,
      loadingStock: false,
      loadingPrice: false,
      loadingError: '',
      submitting: false,
      assemblyOrderItemList: []
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.StockFaceTypeLookup = StockFaceTypeLookup
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  watch: {
    'orderItemData.stockId': function() {
      this.loadStockData()
    },
    initialWidth: function() {
      this.assemblySizeUpdated()
    },
    initialHeight: function() {
      this.assemblySizeUpdated()
    },
    initialRadius: function() {
      // Note: We don't currently support radius in layered assemblies
      this.assemblySizeUpdated()
    }
  },
  mounted() {
    this.loading = true
    this.loadingError = ''

    Promise.all([
      this.$apiService.inventoryService.getStockList({}, { isActive: 1 }),
      this.$apiService.pricingService.getStockPriceGroupList(),
      this.$apiService.orderEditorService.getOrderEditorDefaultEdgeworkList()
    ])
    .then(results => {
      
      this.stockList = results[0].data
      this.assemblyStockList = this.stockList.filter(stock => {
        if(stock.type != InventoryStockTypes.assembly)
          return false

        // Layered only assemblies
        if(stock.assemblyData) {
          if(stock.assemblyData.structureType != StockAssemblyStructureTypes.layered)
            return false
        } else {
          return false
        }

        // Assembly must be square metres for the order editor
        return stock.uomType == UOMTypes.metresq
      })
      this.assemblyStockLookupList = this.assemblyStockList.map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })

      this.priceGroupList = results[1].data
      this.globalDefaultEdgeworkList = results[2].data

      let defaultPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.isDefault)
      if(defaultPriceGroup)
        this.defaultPriceGroupId = defaultPriceGroup.id
    })
    .catch(err => {
      this.loadingError = 'Error loading data'
    })
    .finally(() => {
      this.loading = false
    })
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Assembly Item' : 'Update Assembly Item'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    submitDisabled() {
      if(this.submitting || this.loading || this.loadingStock || !this.hasStock || this.loadingPrice)
        return true

      // Check if any child items are loading
      if(this.isCreateMode) {
        if(this.assemblyOrderItemList.some(item => item.loadingStock || item.loadingPrice))
          return true
      }
      return false
    },
    hasStock() {
      return this.orderItemData.stockId != null
    },
    isRectangleShape() {
      return this.shapeType == LibraryItemShapeTypes.rectangle
    },
    isCircleShape() {
      return this.shapeType == LibraryItemShapeTypes.circle
    },
    isSemiCircleShape() {
      return this.shapeType == LibraryItemShapeTypes.semiCircle
    },
    isOvalShape() {
      return this.shapeType == LibraryItemShapeTypes.oval
    },
    isCustomShape() {
      return this.shapeType == LibraryItemShapeTypes.custom
    },
    isStockUsingFaces() {
      if(!this.hasStock)
        return false
      if(!this.orderItemData.stock)
        return false

      return this.orderItemData.stock.isStockUsingFaces
    },
    isStockUsingGrain() {
      if(!this.hasStock)
        return false
      if(!this.orderItemData.stock)
        return false

      return this.orderItemData.stock.isStockUsingGrain
    },
    isSizeEditable() {
      if(!this.hasStock || !this.orderItemData.stock)
        return false

      var assemblyData = this.orderItemData.stock.assemblyData || {}

      return !!assemblyData.isSizeEditable
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.dialogForm = { }

      this.shapeType = options.shapeType || LibraryItemShapeTypes.rectangle
      this.initialWidth = options.initialWidth || 1000
      this.initialHeight = options.initialHeight || 1000
      this.initialRadius = options.initialRadius || 500
      this.isStamped = !!options.isStamped

      this.orderItemData.description = options.description || ''
      this.orderItemData.mark = options.mark || ''
      this.orderItemData.notes = options.notes || ''
      this.orderItemData.quantity = parseInt(options.quantity || 1)
      this.orderItemData.unitPrice = options.unitPrice
      this.orderItemData.unitPriceOverridden = !!options.unitPriceOverridden

      this.orderItemData.grainDirection = options.grainDirection || StockGrainDirectionTypes.vertical
      this.orderItemData.stockFaceSideUpType = options.stockFaceSideUpType || StockFaceTypes.smooth

      if(options.glassLevelId) {
        this.orderItemData.glassLevelId = parseInt(options.glassLevelId)
        this.orderItemData.glassLevel = _.clone(options.glassLevel || {})
      }

      if(options.stockId)
        this.orderItemData.stockId = parseInt(options.stockId)
      if(options.stockPriceGroupId)
        this.orderItemData.stockPriceGroupId = parseInt(options.stockPriceGroupId)

      if(!this.isCreateMode) {
        this.assemblyOrderItemList = _.cloneDeep(options.assemblyOrderItemList || [])
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.dialogForm = this.clearFormResponse()

      // Run checks
      // Quantity cannot be <= 0
      // If create mode: Initial size must be valid
      // Stock, pricegroup must be set
      if(this.orderItemData.quantity <= 0)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'quantity', 'Quantity must be greater than 0')
      if(!this.orderItemData.stockId)
        this.dialogForm = this.addFormFieldError(this.dialogForm, 'stockId', 'Stock must be set')

      if(this.isCreateMode && (this.isRectangleShape || this.isOvalShape)) {
        if(parseInt(this.initialHeight || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialHeight', 'Initial height must be valid')
        if(parseInt(this.initialWidth || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialWidth', 'Initial width must be valid')
      }
      if(this.isCreateMode && (this.isCircleShape || this.isSemiCircleShape)) {
        if(parseInt(this.initialRadius || 0) <= 0)
          this.dialogForm = this.addFormFieldError(this.dialogForm, 'initialRadius', 'Initial radius must be valid')
      }

      // Go through all layer items & run checks
      this.assemblyOrderItemList.forEach((orderItem, itemIndex) => {
        // Validate stock & price group
        if(orderItem.stockId == null) {
          this.dialogForm = this.addFormFieldError(this.dialogForm, `item${itemIndex}-stockId`, 'Stock must be set')
        }
        if(this.orderItemIsGlass(orderItem) && this.isCreateMode) {
          if(orderItem.stockPriceGroupId == null) {
            this.dialogForm = this.addFormFieldError(this.dialogForm, `item${itemIndex}-stockPriceGroupId`, 'Price group must be set')
          }

          if(parseInt(orderItem.height || 0) <= 0)
            this.dialogForm = this.addFormFieldError(this.dialogForm, `item${itemIndex}-height`, 'Item height must be valid')
          if(parseInt(orderItem.width || 0) <= 0)
            this.dialogForm = this.addFormFieldError(this.dialogForm, `item${itemIndex}-width`, 'Item width must be valid')
        }
      })

      if(this.formResponseHasErrors(this.dialogForm))
        return

      var itemUpdateData = {
        stockId: this.orderItemData.stockId,
        stockPriceGroupId: this.orderItemData.stockPriceGroupId,
        description: this.orderItemData.description,
        mark: this.orderItemData.mark,
        notes: this.orderItemData.notes,
        quantity: this.orderItemData.quantity,
        stock: this.orderItemData.stock,
        stockPriceGroup: this.orderItemData.stockPriceGroup,
        unitPriceOverridden: this.orderItemData.unitPriceOverridden,
        unitPrice: this.orderItemData.unitPrice
      }

      // Child item list
      itemUpdateData.assemblyOrderItemList = _.cloneDeep(this.assemblyOrderItemList)

      this.modalClosed(itemUpdateData)
      this.hide()
    },
    loadStockData() {
      if(this.orderItemData.stockId == null) {
        this.orderItemData.stock = null
        this.orderItemData.unitPrice = 0
        this.orderItemData.unitPriceOverridden = false
        this.orderItemData.glassLevelId = null
        this.orderItemData.glassLevel = null
        return
      }

      this.loadingStock = true
      this.orderItemData.stock = null
      if(!this.orderItemData.unitPriceOverridden)
        this.orderItemData.unitPrice = 0

      Promise.all([
        this.$apiService.inventoryService.getStock(this.orderItemData.stockId),
        this.$apiService.orderEditorService.getStockOrderEditorDefaultEdgeworkList(this.orderItemData.stockId)
      ])
      .then(results => {

        this.orderItemData.stock = results[0].data
        this.orderItemData.description = this.orderItemData.stock.name
        this.stockDefaultEdgeworkList = results[1].data

        this.queryStockPrice()

        // Set default face up if a new glass item
        if(this.isCreateMode && this.orderItemData.stock.isStockUsingFaces) {
          this.orderItemData.stockFaceSideUpType = this.orderItemData.stock.defaultFaceSideUp
        }

        // Convert assembly data into child order items
        // Don't do this when loading an existing assembly
        if(this.isCreateMode)
          this.setupOrderItemsFromAssembly()

      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loadingStock = false
      })
    },
    stockItemUpdated() {
      // Reset some details for the order item
      this.orderItemData.glassLevelId = null
      this.orderItemData.glassLevel = null
    },
    queryStockPrice() {
      if(this.orderItemData.stock == null)
        return

      this.loadingPrice = true

      // Pricing lookup
      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$props.contactId,
        defaultSalesRateId: this.$props.defaultSalesRateId,
        stockId: this.orderItemData.stockId,
        thickness: this.orderItemData.stock.thickness
      }

      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        if(!this.orderItemData.unitPriceOverridden)
          this.orderItemData.unitPrice = parseFloat(response.data.price.value)

        this.orderItemData.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.orderItemData.minChargeQuantity = parseFloat(response.data.price.minChargeQuantity)
        this.orderItemData.minChargeInvoice = parseFloat(response.data.price.minChargeInvoice)

        if(response.data.price.error) {
          // Pricing error
          this.orderItemData.priceError = response.data.price.error
        }
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loadingPrice = false
      })
    },
    selectGlassLevel(evt) {
      this.$refs.glassLevelSelectPopover.displayFromElement(evt.target, this.orderItemData.stockId)
    },
    glassLevelSelected(glassLevelInfo) {
      this.orderItemData.glassLevelId = glassLevelInfo.id
      this.orderItemData.glassLevel = _.clone(glassLevelInfo)
    },
    overrideUnitPrice() {
      this.orderItemData.unitPriceOverridden = true
    },
    resetUnitPrice() {
      this.orderItemData.unitPriceOverridden = false
      this.queryStockPrice()
    },
    setupOrderItemsFromAssembly() {
      var assemblyData = this.orderItemData.stock.assemblyData || {}

      if(parseInt(assemblyData.defaultWidth) > 0)
        this.initialWidth = parseInt(assemblyData.defaultWidth)
      if(parseInt(assemblyData.defaultHeight) > 0)
        this.initialHeight = parseInt(assemblyData.defaultHeight)

      this.assemblyOrderItemList = []
      this.buildAssemblyOrderItemList(assemblyData.itemList || [])
    },
    buildAssemblyOrderItemList(assemblyItemList, parentOrderItemId = null, level = 0) {
      // Convert assembly items into order items
      assemblyItemList.forEach(assemblyItemData => {
        let childCount = this.assemblyOrderItemList.filter(item => item.parentOrderItemId == parentOrderItemId).length

        let itemId = uuidv4()

        let outputOrderItem = {
          id: itemId,
          assemblyLinkUUID: itemId,
          orderItemId: itemId,
          parentOrderItemId: parentOrderItemId,
          stockType: assemblyItemData.stockType,
          assemblyDescription: assemblyItemData.description,
          description: '',
          mark: assemblyItemData.description,
          notes: '',
          stockId: null,
          stock: {},
          stockPriceGroup: {},
          quantity: 1,
          units: assemblyItemData.quantity,
          stockPriceGroupId: null,
          width: this.initialWidth,
          height: this.initialHeight,
          stockFaceSideUpType: null,
          grainDirection: null,
          unitPriceOverridden: false,
          unitPrice: null,
          isStamped: false,
          loadingStock: false,
          loadingPrice: false,
          level: level,
          levelIndex: childCount,
          orderEntryConfigData: {
            assemblyItemData: assemblyItemData
          }
        }

        // If only 1 stock item set, set that as the default
        if(_.isArray(assemblyItemData.stockList) && assemblyItemData.stockList.length == 1) {
          outputOrderItem.stockId = parseInt(assemblyItemData.stockList[0])
        }

        if(assemblyItemData.isUnitPriceOverridden) {
          outputOrderItem.unitPriceOverridden = true
          outputOrderItem.unitPrice = parseFloat(assemblyItemData.unitPrice || 0)
        }

        // Query stock data for the item
        this.orderItemStockItemUpdated(outputOrderItem)

        this.assemblyOrderItemList.push(outputOrderItem)

        // Go through children
        this.buildAssemblyOrderItemList(assemblyItemData.childItems || [], outputOrderItem.id, level + 1)
      })
    },
    orderItemStockLookupList(assemblyOrderItem) {
      return this.stockList.filter(stock => stock.type == assemblyOrderItem.stockType).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    orderItemPriceGroupLookupList(assemblyOrderItem) {
      var availableStockPriceGroups = []

      if(assemblyOrderItem.stock && assemblyOrderItem.stock.glassSalesPriceList) {
        availableStockPriceGroups = assemblyOrderItem.stock.glassSalesPriceList.filter(salesPrice => salesPrice.isActive).map(salesPrice => {
          return {
            id: salesPrice.stockPriceGroupId,
            name: salesPrice.name
          }
        })
      }

      return availableStockPriceGroups
      .filter(priceGroup => {
        return this.priceGroupList.some(pg => pg.id == priceGroup.id)
      })
      .map(priceGroup => {
        return {
          value: priceGroup.id,
          name: priceGroup.name
        }
      })
    },
    orderItemHasStock(assemblyOrderItem) {
      return assemblyOrderItem.stockId != null
    },
    orderItemIsGlass(assemblyOrderItem) {
      return assemblyOrderItem.stockType == InventoryStockTypes.glass
    },
    orderItemIsGlassService(assemblyOrderItem) {
      return assemblyOrderItem.stockType == InventoryStockTypes.glassService
    },
    orderItemIsGeneral(assemblyOrderItem) {
      return assemblyOrderItem.stockType == InventoryStockTypes.general
    },
    orderItemIsParent(assemblyOrderItem) {
      return assemblyOrderItem.parentOrderItemId == null
    },
    orderItemIsStampAvailable(assemblyOrderItem) {
      if(!this.editorConfigData)
        return false
      if(!this.editorConfigData.orderStampConfig)
        return false

      return FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.priceGroupFilter, parseInt(assemblyOrderItem.stockPriceGroupId))
    },
    orderItemIsMarkAvailable(assemblyOrderItem) {
      return assemblyOrderItem.stockType == InventoryStockTypes.glass || assemblyOrderItem.stockType == InventoryStockTypes.general || assemblyOrderItem.stockType == InventoryStockTypes.assembly
    },
    orderItemStockItemUpdated(assemblyOrderItem) {
      if(assemblyOrderItem.stockId == null) {
        assemblyOrderItem.stock = null
        assemblyOrderItem.unitPrice = 0
        assemblyOrderItem.unitPriceOverridden = false
        assemblyOrderItem.glassLevelId = null
        assemblyOrderItem.glassLevel = null
        return
      }

      assemblyOrderItem.loadingStock = true
      assemblyOrderItem.stock = null
      if(!assemblyOrderItem.unitPriceOverridden)
        assemblyOrderItem.unitPrice = 0

      Promise.all([
        this.$apiService.inventoryService.getStock(assemblyOrderItem.stockId),
        this.$apiService.orderEditorService.getStockOrderEditorDefaultEdgeworkList(assemblyOrderItem.stockId)
      ])
      .then(results => {

        assemblyOrderItem.stock = results[0].data
        assemblyOrderItem.description = assemblyOrderItem.stock.name
        this.stockDefaultEdgeworkList = results[1].data

        // Set default price group for the stock (if it has pricing available)
        if(assemblyOrderItem.stockPriceGroupId == null) {
          if(assemblyOrderItem.stock.glassSalesPriceList) {
            if(assemblyOrderItem.stock.glassSalesPriceList.find(price => price.isActive && price.stockPriceGroupId == this.defaultPriceGroupId) != null) {

              assemblyOrderItem.stockPriceGroupId = this.defaultPriceGroupId
              this.orderItemPriceGroupUpdated(assemblyOrderItem, assemblyOrderItem.stockPriceGroupId)
            }
          }
        } else {
          this.orderItemQueryPrice(assemblyOrderItem)
        }

        // Set default face up if a new glass item
        if(this.isCreateMode && assemblyOrderItem.stock.isStockUsingFaces) {
          assemblyOrderItem.stockFaceSideUpType = assemblyOrderItem.stock.defaultFaceSideUp
        }

      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        assemblyOrderItem.loadingStock = false
      })
    },
    orderItemPriceGroupUpdated(assemblyOrderItem, stockPriceGroupId) {
      assemblyOrderItem.stockPriceGroup = this.priceGroupList.find(priceGroup => priceGroup.id == stockPriceGroupId)

      // Default stamp setting
      if(this.orderItemIsStampAvailable(assemblyOrderItem)) {

        // Stamps are on by default, unless explicitly not required for a contact
        // Auto apply needed?
        if(this.editorConfigData.orderStampConfig.isAutoApply) {
          assemblyOrderItem.isStamped = true
          if(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter && this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter.type == FilterOptionTypes.include) {
            assemblyOrderItem.isStamped = !FilterUtils.itemPassesFilter(this.editorConfigData.orderStampConfig.defaultStampNotRequiredContactFilter, this.contactId)
          }
        }
      }

      this.orderItemQueryPrice(assemblyOrderItem)
    },
    orderItemQueryPrice(assemblyOrderItem) {
      if(assemblyOrderItem.stock == null)
        return

      // Ignore services
      if(this.orderItemIsGlassService(assemblyOrderItem))
        return

      assemblyOrderItem.loadingPrice = true

      // Pricing lookup
      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$props.contactId,
        defaultSalesRateId: this.$props.defaultSalesRateId,
        stockId: assemblyOrderItem.stockId,
        stockPriceGroupId: assemblyOrderItem.stockPriceGroupId,
        thickness: assemblyOrderItem.stock.thickness
      }

      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        if(!assemblyOrderItem.unitPriceOverridden)
          assemblyOrderItem.unitPrice = parseFloat(response.data.price.value)

        assemblyOrderItem.minChargePrice = parseFloat(response.data.price.minChargePrice)
        assemblyOrderItem.minChargeQuantity = parseFloat(response.data.price.minChargeQuantity)
        assemblyOrderItem.minChargeInvoice = parseFloat(response.data.price.minChargeInvoice)

        if(response.data.price.error) {
          // Pricing error
          assemblyOrderItem.priceError = response.data.price.error
        }
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        assemblyOrderItem.loadingPrice = false
      })
    },
    assemblyOrderItemStyle(assemblyOrderItem) {
      return {
        'padding-left': `${assemblyOrderItem.level * 25}px`
      }
    },
    assemblySizeUpdated() {
      try {
        // Adjust all child items taking into account the edge offsets
        this.assemblyOrderItemList.forEach(orderItem => {
          let itemAssemblyData = orderItem.orderEntryConfigData.assemblyItemData || {}

          if(this.orderItemIsGlass(orderItem)) {
            let itemWidth = this.initialWidth
            let itemHeight = this.initialHeight

            if(_.isArray(itemAssemblyData.edgeOffsetList) && itemAssemblyData.edgeOffsetList.length == 4) {
              itemWidth -= (parseInt(itemAssemblyData.edgeOffsetList[1]) + parseInt(itemAssemblyData.edgeOffsetList[3]))    // Minus left & right offsets
              itemHeight -= (parseInt(itemAssemblyData.edgeOffsetList[0]) + parseInt(itemAssemblyData.edgeOffsetList[2]))    // Minus top & bottom offsets
            }

            orderItem.width = itemWidth
            orderItem.height = itemHeight
          }
        })
      } catch(err) {
        console.error(err)
      }
    }
  }
}
</script>